/**
 * KlantEigenItemCard — los, klant-eigen item (ondertapijt, spanbanden, …) als
 * genummerd item à €0 op order-niveau toevoegen. Gedeeld door de legacy- en de
 * Werkbank-render van de voorinspectie.
 */

import { useState } from 'react';
import { WbButton, WbFlag, WbInput, WbToggleGroup } from './wb';
import { salesforceClient } from '../lib/salesforce/client';
import type { VoorinspectieItem } from '../lib/salesforce';

const KLANTEIGEN_SNELKEUZE = ['Ondertapijt', 'Spanbanden'];

/**
 * Card om een LOS, KLANT-EIGEN item dat bij de klant tussen de kleden zat
 * (ondertapijt, spanbanden, …) als genummerd item à €0 op ORDER-NIVEAU toe te
 * voegen. Het komt NIET op de factuur, slaat alle was-/maakstappen over en gaat
 * bij afleveren weer mee terug. Type via snelknoppen + 'Overig' (vrij veld).
 */
export function KlantEigenItemCard({
  item,
  onAdded,
}: {
  item: VoorinspectieItem;
  onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [keuze, setKeuze] = useState('Ondertapijt');
  const [overig, setOverig] = useState('');
  const [lengte, setLengte] = useState('');
  const [breedte, setBreedte] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ nummer: string; naam: string } | null>(null);

  const isOverig = keuze === 'Overig';
  const omschrijving = (isOverig ? overig : keuze).trim();

  const handleAdd = async () => {
    if (!omschrijving) {
      setError(isOverig ? 'Vul in wat het is.' : 'Kies een type.');
      return;
    }
    const l = lengte.trim() ? Number(lengte) : undefined;
    const b = breedte.trim() ? Number(breedte) : undefined;
    if ((l === undefined) !== (b === undefined)) {
      setError('Vul lengte én breedte in, of laat beide leeg.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await salesforceClient.addKlantEigenItem(item.werkOrderId, {
        omschrijving,
        lengteCm: l,
        breedteCm: b,
      });
      if (!res.success || !res.data) {
        setError(res.message || 'Toevoegen mislukt');
        return;
      }
      setCreated({ nummer: res.data.itemNummer || '—', naam: omschrijving });
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setBusy(false);
    }
  };

  // Succes-state: toon het WI-nummer groot zodat de medewerker het item labelt.
  if (created) {
    return (
      <div className="bg-wb-vlak border-wb border-wb-rand rounded-wb px-3 py-3 text-center space-y-1">
        <p className="text-sm text-wb-inkt2">{created.naam} toegevoegd als:</p>
        <p className="font-mono text-3xl font-semibold text-wb-inkt">{created.nummer}</p>
        <p className="text-sm text-wb-inkt2">Label het item met dit nummer en pak het apart in.</p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="bg-wb-vlak border-wb border-wb-rand rounded-wb px-3 py-3 space-y-2">
        <p className="text-base font-semibold text-wb-inkt">Los item van de klant erbij?</p>
        <p className="text-sm text-wb-inkt2">Ondertapijt, spanbanden, kussens… tag het à €0: niet op de factuur, gaat mee terug.</p>
        <WbButton variant="secondary" onClick={() => setOpen(true)}>Los item taggen</WbButton>
      </div>
    );
  }

  return (
    <div className="bg-wb-vlak border-wb border-wb-rand rounded-wb px-3 py-3 space-y-3">
      <h3 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Los item taggen</h3>
      {error && <WbFlag kind="geld">{error}</WbFlag>}
      <WbToggleGroup
        label="Wat is het?"
        options={[...KLANTEIGEN_SNELKEUZE, 'Overig'].map((o) => ({ value: o, label: o }))}
        value={keuze}
        onChange={setKeuze}
      />
      {isOverig && (
        <WbInput
          label="Wat is het?"
          value={overig}
          disabled={busy}
          onChange={(e) => setOverig(e.target.value)}
          placeholder="bijv. 2 kussens, hondenmand…"
        />
      )}
      <p className="text-sm text-wb-inkt2">Afmetingen zijn optioneel (laat leeg als onbekend).</p>
      <div className="grid grid-cols-2 gap-2">
        <WbInput label="Lengte" eenheid="cm" mono type="number" inputMode="numeric" min={30} max={800} value={lengte} disabled={busy} onChange={(e) => setLengte(e.target.value)} placeholder="200" />
        <WbInput label="Breedte" eenheid="cm" mono type="number" inputMode="numeric" min={30} max={800} value={breedte} disabled={busy} onChange={(e) => setBreedte(e.target.value)} placeholder="150" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <WbButton variant="secondary" onClick={() => setOpen(false)} disabled={busy}>Annuleren</WbButton>
        <WbButton onClick={handleAdd} disabled={busy} isLoading={busy}>Toevoegen (€0)</WbButton>
      </div>
    </div>
  );
}
